import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { clearNodeCache } from '@leagueplatform/masonry-engine';
import { Masonry } from '@leagueplatform/masonry';
import {
  DOCUMENT_FILTERS,
  SEARCH_DOCUMENTS,
} from '@leagueplatform/care-documents-api';
import { PlanSwitcherChrome } from 'components/plan-switcher-chrome/plan-switcher-chrome.component';
import { MASONRY } from 'common/constants';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { usePlanSwitcherGateCompletion } from 'hooks/use-plan-switcher-gate-completion/use-plan-switcher-gate-completion.hook';
import { useResetUnreadMessageCount } from 'hooks/use-unread-message-count/use-unread-message-count.hook';

// Document Center queries that hold plan-scoped data. The remote-config key is
// not exported by the SDK. Prefix matches, so every search page and filter
// combination goes with them.
const DOCUMENTS_QUERY_KEYS = [
  [SEARCH_DOCUMENTS],
  [DOCUMENT_FILTERS],
  ['GET_REMOTE_CONFIG'],
];

export const SwitchAccountsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const resetUnreadMessageCount = useResetUnreadMessageCount();
  // On completion, drop the cached Home Masonry tree, then route Home in-app
  // (NOT a full reload — a reload re-triggers the post-login Plan Switcher gate,
  // which surfaces on every app entry). The active plan is resolved server-side
  // from the session, so a fresh Home request returns the newly selected plan;
  // removing the node cache forces that fresh fetch on mount instead of serving
  // the cached previous-plan tree. 'remove' over 'invalidate' avoids a brief
  // keepPreviousData flash of the old tree, and descendants clears the child
  // nodes too. replace so Back doesn't return into the switcher. Mirrors the
  // native in-app Home refresh on switch.
  const handleComplete = useCallback(() => {
    clearNodeCache({
      id: MASONRY.ROOT_NODE_ID_HOME_PAGE,
      queryClient,
      self: true,
      descendants: true,
      strategy: 'remove',
    });
    // The unread messaging count is scoped to the active plan (resolved
    // server-side from the session), so the cached one now belongs to a plan the
    // member has left. Dropped rather than invalidated for the same reason as the
    // node cache above: invalidating would keep the old plan's indicator on
    // screen until the refetch landed.
    resetUnreadMessageCount();
    // The documents list and filters come from the active plan. Removed rather
    // than invalidated: nothing on this page observes them, so an invalidate
    // would leave the old plan's list to flash when the Document Center next
    // mounts. The remote config is cached forever, so it would never refetch.
    DOCUMENTS_QUERY_KEYS.forEach((queryKey) => {
      queryClient.removeQueries({ queryKey });
    });
    // The selected tab and filters live in a module-global store that outlives
    // the Document Center, so they would carry the old plan's choices over.
    // Imported dynamically: this page is in the entry chunk and care-documents
    // is a lazy route, so a static import would pull it into the entry chunk.
    // The route's lazy() awaits the same module, so the reset lands before the
    // Document Center can mount.
    import('@leagueplatform/care-documents').then(
      ({ resetCareDocumentsFilters }) => resetCareDocumentsFilters(),
    );
    navigate(APP_MANAGED_ROUTES.HOME, { replace: true });
  }, [navigate, queryClient, resetUnreadMessageCount]);
  usePlanSwitcherGateCompletion(handleComplete);

  return (
    <PlanSwitcherChrome
      screenName="Switch Accounts"
      titleMessageId="STR_SWITCH_ACCOUNTS"
    >
      <Masonry
        rootNodeId={MASONRY.ROOT_NODE_ID_PLAN_SWITCHER}
        // SDK reload treats undefined → {} as changed, but its query key does
        // not. Start with {} so the first retry actually refetches the root.
        initialQueryParams={{}}
      />
    </PlanSwitcherChrome>
  );
};
